import AsyncSelect from "react-select/async";
import { Button } from "react-bootstrap";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlusLg } from "react-bootstrap-icons";
import {
  EntitySelectFieldCreateModalContext,
  EntitySelectFieldModel,
} from "./entitySelectFieldModel";
import useEntitySelect from "../../../../../../hooks/useEntitySelect";
import { relationSchema } from "../../../../../relation/domain/models/RelationModel";
import useCrudForm from "../../../../../../hooks/useCrudForm";
import { LayoutModal } from "../../../../modules/commons/layoutModal/layoutModal";
import { useEffect, useRef, useCallback } from "react";

export const EntitySelectField = ({
  entity,
  prefix,
  createMutation,
  formTemplate,
  onBlur,
  onChange,
  isInvalid,
  value,
  page,
  limit,
  clearable = false,
  schema,
  filters,
  labelPrefix = "name",
  isDisabled = false,
  isDisabledButLoadOptions = false,
  isFake = false,
  defaultValueLabel,
}: EntitySelectFieldModel & {
  defaultValueLabel?: string;
  isDisabledButLoadOptions?: boolean;
}) => {
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const {
    ref,
    options,
    loadOptions,
    loading,
    addOption,
    findByIdAndAdd,
    setDefaultOption,
  } = useEntitySelect({
    entity,
    labelPrefix: labelPrefix,
    prefix,
    schema,
    page,
    limit,
    filters,
    value,
    isDisabled,
  });

  const { t } = useTranslation("common");

  const onChangeRef = useRef(onChange);
  const lastValueRef = useRef(value);
  const hasSetDefaultOptionRef = useRef(false);

  // Update the ref when onChange changes
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Memoized onChange handler to prevent multiple calls with same value
  const handleChange = useCallback(
    // eslint-disable-next-line
    (newValue: any) => {
      if (onChangeRef.current && newValue !== lastValueRef.current) {
        lastValueRef.current = newValue;
        onChangeRef.current(newValue);
      }
    },
    [onChangeRef],
  );

  // Effect to handle value clearing from external source
  useEffect(() => {
    if (value === null || value === undefined) {
      // Clear the select field when value becomes null/undefined
      if (ref.current) {
        ref.current.setValue(null, "select-option");
      }
      lastValueRef.current = null;
    }
  }, [value]);

  useEffect(() => {
    if (options.length === 1 && onChangeRef.current) {
      handleChange(options[0]);
    }

    if (options.length === 0 || value === undefined || value === null) {
      return;
    }

    if (
      defaultValueLabel &&
      value &&
      options.find((option) => option.value === value) === undefined &&
      !hasSetDefaultOptionRef.current
    ) {
      setDefaultOption(value?.toString() ?? "", defaultValueLabel);
      hasSetDefaultOptionRef.current = true;
    }

    if (
      !defaultValueLabel &&
      value &&
      options.find((option) => option.value === value) === undefined
    ) {
      findByIdAndAdd(value?.toString());
    }
  }, [options, defaultValueLabel, value, handleChange]);

  useEffect(() => {
    if (
      isFake &&
      ref.current?.getValue() !== undefined &&
      ref.current?.getValue().length > 0
    ) {
      ref.current?.setValue(null, "select-option");
    }
  }, [isFake, ref.current?.getValue()]);

  return (
    <div className={`entity-select-wrapper${isInvalid ? " is-invalid" : ""}`}>
      <AsyncSelect
        placeholder={t("Select...")}
        className={"entity-select"}
        ref={ref}
        loadOptions={loadOptions}
        defaultOptions={options}
        cacheOptions
        isLoading={loading}
        onBlur={onBlur}
        onChange={onChange}
        isClearable={clearable}
        isDisabled={isDisabled || isDisabledButLoadOptions}
        classNamePrefix={"entity-select"}
      />
      {createMutation && (
        <Button
          className="entity-select-add-button"
          onClick={() => {
            navigate(`${pathname}/add`);
          }}
          size="sm"
        >
          <PlusLg size={16} color="white" />
        </Button>
      )}
      <Outlet
        context={{
          schema: relationSchema,
          prefix: prefix,
          createMutation: createMutation,
          formTemplate: formTemplate,
          update: (result: { id: string; name: string }) => {
            const newOption = {
              value: result.id,
              label: result.name,
            };

            addOption(result.id, result.name);
            ref.current?.setValue(newOption, "select-option");
            if (onChange) {
              onChange(newOption);
            }
          },
        }}
      />
    </div>
  );
};

export const EntitySelectFieldCreateModal = () => {
  const { schema, prefix, createMutation, update, formTemplate } =
    useOutletContext<EntitySelectFieldCreateModalContext>();

  const { t } = useTranslation("common");

  const navigate = useNavigate();

  const crudForm = useCrudForm({
    schema: schema,
    prefix: prefix,
    createMutation: createMutation,
    updateMutation: createMutation,
    onAfterSubmit: (result) => {
      update(result);
      navigate(-1);
    },
    formTemplate: formTemplate,
  });

  return (
    <LayoutModal
      title={t(`crud.${prefix}.modal.create.title`)}
      show={true}
      onHide={() => {
        navigate(-1);
      }}
      actions={crudForm.formSubmit}
    >
      {crudForm.form}
    </LayoutModal>
  );
};
